import { useCallback, useMemo } from 'react';
import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ProjectStatus } from 'epd-validator-shared';

import { toaster } from '../components/ui/toaster';
import * as api from '../lib/api';
import type { Project, ProjectPermission } from '../types';
import { DocumentType } from '../types';

/**
 * Hook for managing a single project's operations and state.
 */
export function useProject(projectId: string) {
  const queryClient = useQueryClient();
  const queryKey = ['project', projectId];
  const projectsListKey = ['projects'];

  const {
    data: project,
    isLoading,
    error,
  } = useQuery<Project>({
    queryKey,
    queryFn: () => api.getProject(projectId),
  });

  // Update Project Name Mutation
  const updateProjectNameMutation = useMutation({
    mutationFn: (name: string) => api.updateProject(projectId, { name }),
    onMutate: async (name: string) => {
      await queryClient.cancelQueries({ queryKey });
      await queryClient.cancelQueries({ queryKey: projectsListKey });

      const previousProject = queryClient.getQueryData<Project>(queryKey);
      const previousProjectsList = queryClient.getQueryData<{ items: Project[] }>(projectsListKey);

      // Optimistically update project name in cache
      queryClient.setQueryData<Project>(queryKey, (current) => (current ? { ...current, name } : current));

      // Optimistically update all cached project lists
      queryClient.setQueryData<{ items: Project[] }>(projectsListKey, (current) =>
        current
          ? {
              ...current,
              items: current.items.map((proj) => (proj.id === projectId ? { ...proj, name } : proj)),
            }
          : current
      );
      return { previousProject, previousProjectsList };
    },
    onError: (_err, name, context) => {
      if (context?.previousProject) {
        queryClient.setQueryData(queryKey, context.previousProject);
      }
      if (context?.previousProjectsList) {
        queryClient.setQueryData(projectsListKey, context.previousProjectsList);
      }
      toaster.error({
        title: 'Update failed',
        description: 'Failed to update the project name',
      });
    },
    onSuccess: () => {
      toaster.success({
        title: 'Name updated',
        description: 'The project name has been updated successfully',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: projectsListKey });
    },
  });

  const deleteProjectMutation = useMutation({
    mutationFn: () => api.deleteProject(projectId),
    onSuccess: () => {
      toaster.success({ title: 'Project deleted', description: 'Your project has been deleted successfully' });
    },
    onError: () => {
      toaster.error({ title: 'Error deleting project', description: 'Failed to delete project. Please try again.' });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: projectsListKey });
    },
  });

  // Leave Project Mutation
  const leaveProjectMutation = useMutation({
    mutationFn: async () => {
      return api.leaveProject(projectId);
    },
    onSuccess: () => {
      toaster.success({ title: 'Left project', description: 'You have successfully left the project' });
    },
    onError: () => {
      toaster.error({ title: 'Error leaving project', description: 'Failed to leave project. Please try again.' });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: projectsListKey });
    },
  });

  // Upload Document Mutation
  const uploadDocumentMutation = useMutation({
    mutationFn: ({ documentType, file }: { documentType: DocumentType; file: File }) =>
      api.uploadValidationDocument(projectId, documentType, file),
    onSuccess: (_data, variables) => {
      toaster.success({
        title: 'Document uploaded',
        description: `${
          variables.documentType === DocumentType.EPD
            ? 'EPD Document'
            : variables.documentType === DocumentType.LCA_REPORT
              ? 'LCA Report'
              : 'Additional Document'
        } uploaded successfully`,
      });
    },
    onError: (_err) => {
      toaster.error({
        title: 'Error uploading document',
        description: 'Failed to upload document. Please try again.',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // Delete Document Mutation
  const deleteDocumentMutation = useMutation({
    mutationFn: (documentId: string) => api.deleteValidationDocument(projectId, documentId),
    onSuccess: () => {
      toaster.success({
        title: 'Document deleted',
        description: 'Document deleted successfully',
      });
    },
    onError: () => {
      toaster.error({
        title: 'Error deleting document',
        description: 'Failed to delete document. Please try again.',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // --- Helper for Project Status Update Mutations ---
  const createStatusUpdateMutationOptions = (
    mutationFn: () => Promise<Project>,
    {
      successTitle,
      successDescription,
      errorTitle,
      errorDefaultMessage,
    }: {
      successTitle: string;
      successDescription: string;
      errorTitle: string;
      errorDefaultMessage: string;
    }
  ) => ({
    mutationFn,
    onSuccess: (data: Project) => {
      queryClient.setQueryData(queryKey, data);
      queryClient.invalidateQueries({ queryKey: projectsListKey });
      toaster.success({ title: successTitle, description: successDescription });
    },
    onError: (error: Error) => {
      toaster.error({ title: errorTitle, description: error.message || errorDefaultMessage });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const submitForVerificationMutation = useMutation<Project, Error, string | undefined>({
    mutationFn: (paymentIntentId?: string) => api.submitProjectForVerification(projectId, paymentIntentId),
    onSuccess: (data: Project) => {
      queryClient.setQueryData(queryKey, data);
      queryClient.invalidateQueries({ queryKey: projectsListKey });
      toaster.success({ title: 'Project Submitted', description: 'Project submitted for verification.' });
    },
    onError: (error: Error) => {
      toaster.error({
        title: 'Submission Failed',
        description: error.message || 'Could not submit project for verification.',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const startReviewMutation = useMutation<Project, Error>(
    createStatusUpdateMutationOptions(() => api.startProjectReview(projectId), {
      successTitle: 'Review Started',
      successDescription: 'Project review has started.',
      errorTitle: 'Failed to Start Review',
      errorDefaultMessage: 'Could not start project review.',
    })
  );

  const approveProjectMutation = useMutation<Project, Error>(
    createStatusUpdateMutationOptions(() => api.approveProject(projectId), {
      successTitle: 'Project Approved',
      successDescription: 'Project has been approved.',
      errorTitle: 'Approval Failed',
      errorDefaultMessage: 'Could not approve project.',
    })
  );

  const requestChangesMutation = useMutation<Project, Error>(
    createStatusUpdateMutationOptions(() => api.requestProjectChanges(projectId), {
      successTitle: 'Changes Requested',
      successDescription: 'Changes have been requested for the project.',
      errorTitle: 'Failed to Request Changes',
      errorDefaultMessage: 'Could not request changes for the project.',
    })
  );

  const checkPermission = useCallback(
    (permission: ProjectPermission): boolean => {
      if (!project || !project.userPermissions) {
        return false;
      }
      return project.userPermissions.includes(permission);
    },
    [project]
  );

  // Project lock logic
  const lockedStatuses = [
    ProjectStatus.PENDING_VERIFICATION,
    ProjectStatus.UNDER_REVIEW,
    ProjectStatus.VERIFIED_APPROVED,
  ];
  const isProjectLocked = useMemo(() => !!project && lockedStatuses.includes(project.status), [project]);

  // Consolidated status update loading state
  const isUpdatingStatus =
    submitForVerificationMutation.isPending ||
    startReviewMutation.isPending ||
    approveProjectMutation.isPending ||
    requestChangesMutation.isPending;

  return {
    project,
    isLoading,
    error,
    updateProjectName: updateProjectNameMutation.mutateAsync,
    deleteProject: deleteProjectMutation.mutateAsync,
    leaveProject: leaveProjectMutation.mutateAsync,
    uploadDocument: (documentType: DocumentType, file: File) =>
      uploadDocumentMutation.mutateAsync({ documentType, file }),
    deleteDocument: deleteDocumentMutation.mutateAsync,
    isLeaving: leaveProjectMutation.isPending,
    checkPermission,
    isProjectLocked,
    submitForVerification: submitForVerificationMutation.mutateAsync,
    startReview: startReviewMutation.mutateAsync,
    approveProject: approveProjectMutation.mutateAsync,
    requestChanges: requestChangesMutation.mutateAsync,
    isUpdatingStatus,
  };
}

export function handleProjectStatusChanged(queryClient: QueryClient, projectId: string, status: ProjectStatus) {
  queryClient.setQueryData<Project | undefined>(['project', projectId], (currentProject) => {
    if (currentProject) {
      return { ...currentProject, status };
    }
    return currentProject;
  });
  queryClient.invalidateQueries({ queryKey: ['projects'] });
}
